import React, { useEffect, useState } from 'react';
import { Shield, Save, AlertCircle, Eye, Plus, Edit2, Trash2 } from 'lucide-react';
import { getMenuPermissions, updateMenuPermissions, getRoles } from '../api';
import { useAuth } from '../AuthContext';

const FALLBACK_ROLES = [
  { value: 'admin', label: 'Admin', color: '#11cdef' },
  { value: 'manager', label: 'Manager', color: '#f59e0b' },
  { value: 'staff', label: 'Staff', color: '#2dce89' },
  { value: 'satgas', label: 'Satgas', color: '#fb6340' },
  { value: 'operator', label: 'Operator', color: '#8965e0' },
  { value: 'teknisi', label: 'Teknisi', color: '#06b6d4' },
];

const ACTIONS = [
  { key: 'can_view', label: 'View', icon: Eye, desc: 'Melihat halaman & data' },
  { key: 'can_create', label: 'Tambah', icon: Plus, desc: 'Menambah data baru' },
  { key: 'can_edit', label: 'Edit', icon: Edit2, desc: 'Mengubah data' },
  { key: 'can_delete', label: 'Hapus', icon: Trash2, desc: 'Menghapus data' },
];

const MENU_ITEMS = [
  { key: 'dashboard', label: 'Dashboard', desc: 'Halaman utama dashboard' },
  { key: 'vehicles', label: 'Kendaraan', desc: 'Data kendaraan armada' },
  { key: 'regions', label: 'Wilayah', desc: 'Wilayah operasional' },
  { key: 'spareparts', label: 'Sparepart', desc: 'Katalog sparepart' },
  { key: 'supplier', label: 'Supplier', desc: 'Master data supplier' },
  { key: 'master_role', label: 'Master Role', desc: 'Pengelolaan role pengguna' },
  { key: 'inventori', label: 'Inventori', desc: 'Manajemen inventori: sparepart, pembelian, verifikasi, retur, opname, penyesuaian, stok keluar, gudang' },
  { key: 'jasa_layanan', label: 'Jasa Layanan', desc: 'Master data jasa layanan & tarif' },
  { key: 'karyawan', label: 'Karyawan', desc: 'Data karyawan' },
  { key: 'ticketing', label: 'Ticketing', desc: 'Permintaan perbaikan dari pengemudi/pemilik kendaraan' },
  { key: 'hris', label: 'HRIS', desc: 'Human Resource Information System: kontrak, cuti, gaji, KPI, pelatihan' },
  { key: 'penugasan', label: 'Penugasan', desc: 'Penugasan karyawan' },
  { key: 'pm', label: 'PM', desc: 'Preventive Maintenance' },
  { key: 'cm', label: 'CM', desc: 'Corrective Maintenance' },
  { key: 'laporan', label: 'Laporan', desc: 'Laporan dan analisa' },
  { key: 'checklist', label: 'Checklist', desc: 'Checklist kendaraan' },
  { key: 'absensi', label: 'Absensi', desc: 'Absensi & kehadiran karyawan' },
  { key: 'jadwal_kerja', label: 'Jadwal Kerja', desc: 'Jadwal kerja harian karyawan' },
  { key: 'pengajuan_lembur', label: 'Pengajuan Lembur', desc: 'Pengajuan & approval lembur' },
  { key: 'laporan_absensi', label: 'Laporan Absensi', desc: 'Laporan & rekap absensi' },
  { key: 'tagihan', label: 'Tagihan', desc: 'Mengelola invoice & tagihan ke pemilik kendaraan' },
  { key: 'ringkasan_hutang', label: 'Ringkasan Hutang', desc: 'Ringkasan & monitoring hutang supplier' },
  { key: 'pembayaran_hutang', label: 'Bayar Hutang', desc: 'Pembayaran hutang ke supplier / pemasok' },
  { key: 'akuntansi', label: 'Akuntansi', desc: 'Akuntansi & laporan keuangan standar' },
  { key: 'activity', label: 'Activity', desc: 'Manajemen activity harian (add, list, laporan)' },
  { key: 'marketing_activity', label: 'Marketing & Sales', desc: 'Aktivitas marketing, client relations, admin support & reporting' },
  { key: 'activity_rd', label: 'Activity R&D', desc: 'Kegiatan Tim Research & Development' },
  { key: 'crm', label: 'CRM', desc: 'Customer Relationship Management: kontak, pipeline, follow-up' },
  { key: 'catatan', label: 'Catatan', desc: 'Mencatat hal-hal yang perlu dicatat' },
  { key: 'users', label: 'User', desc: 'Management user' },
  { key: 'bagian', label: 'Bagian', desc: 'Management bagian' },
  { key: 'hak_akses_menu', label: 'Hak Akses Menu', desc: 'Pengaturan hak akses' },
  { key: 'system_logs', label: 'Log Sistem', desc: 'Catatan aktivitas pengguna' },
];

const ACTION_COLORS = {
  can_view: '#11cdef',
  can_create: '#2dce89',
  can_edit: '#fb6340',
  can_delete: '#f5365c',
};

export default function HakAksesMenu() {
  const { hasMenuAction } = useAuth();
  const [permissions, setPermissions] = useState({});
  const [roles, setRoles] = useState(FALLBACK_ROLES);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    Promise.all([getMenuPermissions(), getRoles()])
      .then(([permRes, rolesRes]) => {
        const permMap = {};
        permRes.data.forEach(p => {
          const baseKey = `${p.role}:${p.menu_key}`;
          permMap[`${baseKey}:can_view`] = p.can_view;
          permMap[`${baseKey}:can_create`] = p.can_create;
          permMap[`${baseKey}:can_edit`] = p.can_edit;
          permMap[`${baseKey}:can_delete`] = p.can_delete;
        });
        setPermissions(permMap);

        if (rolesRes.data && rolesRes.data.length > 0) {
          setRoles(rolesRes.data.map(r => ({
            value: r.name,
            label: r.display_name,
            color: r.color || '#6b7280',
          })));
        }
      })
      .catch(err => {
        console.error(err);
        setError('Gagal memuat data hak akses');
      })
      .finally(() => setLoading(false));
  }, []);

  const togglePermission = (role, menuKey, action) => {
    const key = `${role}:${menuKey}:${action}`;
    setPermissions(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
    setSaved(false);
  };

  const getPermission = (role, menuKey, action) => {
    return permissions[`${role}:${menuKey}:${action}`] === true;
  };

  const isAllChecked = (role, menuKey) => {
    return ACTIONS.every(a => getPermission(role, menuKey, a.key));
  };

  const toggleAllForMenu = (role, menuKey, checked) => {
    const newPerms = { ...permissions };
    ACTIONS.forEach(a => {
      newPerms[`${role}:${menuKey}:${a.key}`] = checked;
    });
    setPermissions(newPerms);
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSaved(false);

    try {
      const payload = [];
      for (const role of roles) {
        const isAdmin = role.value === 'admin';
        for (const menu of MENU_ITEMS) {
          payload.push({
            role: role.value,
            menu_key: menu.key,
            can_view: isAdmin || getPermission(role.value, menu.key, 'can_view'),
            can_create: isAdmin || getPermission(role.value, menu.key, 'can_create'),
            can_edit: isAdmin || getPermission(role.value, menu.key, 'can_edit'),
            can_delete: isAdmin || getPermission(role.value, menu.key, 'can_delete'),
          });
        }
      }
      await updateMenuPermissions(payload);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Gagal menyimpan hak akses');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="page">
        <div className="page-header">
          <h2>Hak Akses Menu</h2>
          <p className="page-desc">Pengaturan hak akses berdasarkan role</p>
        </div>
        <div className="page-loading">Memuat data hak akses...</div>
      </div>
    );
  }

  const actionIcon = (actionKey) => {
    const action = ACTIONS.find(a => a.key === actionKey);
    if (!action) return null;
    const Icon = action.icon;
    return <Icon size={14} />;
  };

  return (
    <div className="page hak-akses-page">
      <div className="page-header">
        <div className="page-header-row">
          <div>
            <h2>Hak Akses Menu</h2>
            <p className="page-desc">Atur hak akses untuk setiap menu & aksi (View, Tambah, Edit, Hapus) berdasarkan role</p>
          </div>
          <div className="page-header-actions">
            {saved && <span className="save-success">✓ Berhasil disimpan</span>}
            {hasMenuAction('hak_akses_menu', 'edit') && (
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                <Save size={16} />
                <span>{saving ? 'Menyimpan...' : 'Simpan Perubahan'}</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="form-error" style={{ marginBottom: 16 }}>
          <AlertCircle size={14} />
          <span>{error}</span>
        </div>
      )}

      <div className="perm-table-wrapper">
        <table className="perm-table">
          <thead>
            <tr>
              <th className="perm-menu-header" style={{ minWidth: 160 }}>Menu</th>
              <th style={{ minWidth: 90, textAlign: 'left' }}>Aksi</th>
              {roles.map(role => (
                <th key={role.value} className="perm-role-header">
                  <span className="role-badge" style={{
                    background: (role.color || '#6b7280') + '18',
                    color: role.color || '#6b7280',
                  }}>
                    {role.label}
                  </span>
                </th>
              ))}
              <th className="perm-desc-header">Keterangan</th>
            </tr>
          </thead>
          <tbody>
            {MENU_ITEMS.map(menu => (
              <React.Fragment key={menu.key}>
                {/* Menu header row */}
                <tr key={`${menu.key}-header`} style={{ background: 'var(--gray-50)' }}>
                  <td className="perm-menu-name" style={{ fontWeight: 700, fontSize: '0.9rem' }}>
                    {menu.label}
                  </td>
                  <td style={{ fontWeight: 600, fontSize: '0.75rem', color: 'var(--gray-400)' }}>
                    Semua Aksi
                  </td>
                  {roles.map(role => (
                    <td key={role.value} className="perm-check-cell">
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={isAllChecked(role.value, menu.key)}
                          onChange={() => toggleAllForMenu(role.value, menu.key, !isAllChecked(role.value, menu.key))}
                          disabled={role.value === 'admin'}
                        />
                        <span className="toggle-slider" />
                      </label>
                    </td>
                  ))}
                  <td className="perm-desc text-muted">{menu.desc}</td>
                </tr>
                {/* Action sub-rows */}
                {ACTIONS.map(action => (
                  <tr key={`${menu.key}-${action.key}`}>
                    <td style={{ paddingLeft: 40, fontSize: '0.8rem', color: 'var(--gray-400)' }}>
                      {/* Empty cell for menu indent */}
                    </td>
                    <td style={{ padding: '8px 16px', borderBottom: '1px solid var(--gray-100)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{
                          width: 28,
                          height: 28,
                          borderRadius: 6,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: ACTION_COLORS[action.key] + '15',
                          color: ACTION_COLORS[action.key],
                        }}>
                          {actionIcon(action.key)}
                        </span>
                        <span style={{ fontWeight: 600, fontSize: '0.82rem', color: 'var(--gray-600)' }}>
                          {action.label}
                        </span>
                      </div>
                    </td>
                    {roles.map(role => (
                      <td key={role.value} className="perm-check-cell" style={{ padding: '8px 16px', borderBottom: '1px solid var(--gray-100)' }}>
                        <label className="toggle-switch">
                          <input
                            type="checkbox"
                            checked={getPermission(role.value, menu.key, action.key)}
                            onChange={() => togglePermission(role.value, menu.key, action.key)}
                            disabled={role.value === 'admin'}
                          />
                          <span className="toggle-slider" />
                        </label>
                      </td>
                    ))}
                    <td className="perm-desc text-muted" style={{ padding: '8px 16px', borderBottom: '1px solid var(--gray-100)', fontSize: '0.78rem' }}>
                      {action.desc}
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <div className="perm-note">
        <Shield size={14} />
        <span>Role Admin memiliki akses penuh ke semua menu & aksi dan tidak dapat diubah. Gunakan toggle "Semua Aksi" untuk mengatur View, Tambah, Edit, Hapus sekaligus.</span>
      </div>
    </div>
  );
}
